import { Play, Pause, RotateCcw, Volume2, Loader2 } from "lucide-react";
import { useVoiceAudio } from "./useVoiceAudio";
import { VoiceWaveform } from "./VoiceWaveform";
import peaks from "./voice-peaks.json";

const VOICES = [
  { id: "pulse", name: "Pulse", epithet: "The signature", desc: "Natural. Balanced. Effortlessly clear.", accent: "#267263", muted: "#b1cbc4", tint: "#e3ece8" },
  { id: "angel", name: "Angel", epithet: "A little warmth", desc: "Warm. Playful. Full of expression.", accent: "#b4553d", muted: "#dbc0b7", tint: "#f3e9e5" },
  { id: "alfred", name: "Alfred", epithet: "Quietly composed", desc: "Refined. Considered. Distinctly British.", accent: "#506476", muted: "#bbc7d0", tint: "#e7ebee" },
  { id: "janett", name: "Janett", epithet: "With a sharp wit", desc: "Sharp. Sarcastic. Unfiltered.", accent: "#99455e", muted: "#d6b8c1", tint: "#f0e5e9" },
].map((v) => ({ ...v, ...peaks[v.id], src: `${process.env.PUBLIC_URL}/voices/${v.id}.mp3` }));
const fmt = (s) => `0:${String(Math.floor(Math.max(0, s))).padStart(2, "0")}`;

export const SignalVoices = () => {
  const player = useVoiceAudio();
  return (
    <section id="voices" data-testid="signal-voices" className="signal-voices signal-chapter">
      <div className="signal-container">
        <div className="signal-section-intro">
          <div>
            <h2 className="signal-eyebrow" data-testid="signal-voices-label">Meet the voices</h2>
            <p className="signal-display" data-testid="signal-voices-headline">The same Pulse One.<br /><span className="signal-muted">A different kind of voice.</span></p>
          </div>
          <p className="signal-intro-note" data-testid="signal-voices-intro"><Volume2 size={20} aria-hidden="true" />Four distinct voices. One bracelet.<br />Find the one that sounds like you.</p>
        </div>
        <div className="signal-voice-ensemble" data-testid="signal-channel-band">
          {VOICES.map((v) => {
            const active = v.id === player.activeId;
            const playing = active && player.status === "playing";
            const loading = active && player.status === "loading";
            const ended = active && player.status === "ended";
            const Icon = loading ? Loader2 : playing ? Pause : ended ? RotateCcw : Play;
            const label = loading ? "Loading…" : playing ? "Pause" : ended ? "Listen again" : active && player.status === "paused" ? "Continue" : `Hear ${v.name}`;
            return (
              <article className="signal-voice" key={v.id} data-testid={`signal-voice-${v.id}`} data-active={active}
                style={{ "--voice-accent": v.accent, "--voice-muted": v.muted, "--voice-tint": v.tint }}>
                <button type="button" className="signal-voice-choice" onClick={() => player.toggle(v)}
                  data-testid={`signal-channel-${v.id}`} data-active={active} aria-pressed={playing}
                  aria-label={`${playing ? "Pause" : "Play"} ${v.name} voice sample`}>
                  <span className="signal-voice-identity">
                    <span className="signal-voice-epithet" data-testid={`signal-voice-epithet-${v.id}`}>{v.epithet}</span>
                    <span className="signal-voice-name" data-testid={`signal-voice-name-${v.id}`}>{v.name}</span>
                    <span className="signal-voice-desc" data-testid={`signal-voice-desc-${v.id}`}>{v.desc}</span>
                  </span>
                  <span className="signal-voice-control">
                    <span className="signal-play-disc" data-testid={active ? "signal-voice-play" : `signal-voice-play-${v.id}`}>
                      <Icon size={22} className={loading ? "signal-spin" : ""} fill={Icon === Play ? "currentColor" : "none"} strokeWidth={1.6} aria-hidden="true" />
                    </span>
                    <span className="signal-listen-label" data-testid={`signal-voice-action-${v.id}`}>{label}</span>
                  </span>
                </button>
                <VoiceWaveform voice={v} player={player} active={active} />
                <div className="signal-voice-meta">
                  <span data-testid={`signal-voice-state-${v.id}`}>{playing ? "Playing" : active && player.status === "paused" ? "Paused" : "Voice sample"}</span>
                  <span data-testid={`signal-voice-time-${v.id}`}>{fmt(active ? player.time : 0)} / {fmt(active && player.duration ? player.duration : v.duration)}</span>
                </div>
              </article>
            );
          })}
        </div>
        {player.error && <p role="alert" className="signal-audio-error" data-testid="signal-voice-error">{player.error}</p>}
        <div className="signal-voices-footnote">
          <p data-testid="signal-voice-setup">Your choice during setup. Yours to change.</p>
          <p data-testid="signal-voice-status" role="status" aria-live="polite">{player.status === "playing" ? `${VOICES.find((v) => v.id === player.activeId).name} is speaking` : "The voices of Ivory Pulse"}</p>
        </div>
      </div>
    </section>
  );
};